import { z } from "zod";

/**
 * This file contains messages that are sent from the Moment frontend to the canvas metadata collab
 * server. The most important messages are:
 *
 *   - `initialize`, which the client sends on connection, to start the collaboration server and
 *     initialize it with the seed data (if it exists) persisted from the Moment GraphQL API.
 *   - `enqueueTransaction`, which the client sends when the user changes the metadata locally,
 *     e.g., changing the canvas title.
 */

export * from "./EnqueueTransactionEvent";
export * from "./InitializeEvent";
export * from "./UnloadEvent";

// Set canvas title
export const setCanvasTitle = z.object({
	operation: z.literal("setCanvasTitle"),
	newTitle: z.string(),
});
export type SetCanvasTitleOperation = z.infer<typeof setCanvasTitle>;
// Archive canvas
export const archiveCanvas = z.object({
	operation: z.literal("archiveCanvas"),
	archived: z.boolean(),
});
export type ArchiveCanvasOperation = z.infer<typeof archiveCanvas>;
// Set canvas template
export const setCanvasTemplate = z.object({
	operation: z.literal("setCanvasTemplate"),
	isTemplate: z.boolean(),
});
export type SetCanvasTemplateOperation = z.infer<typeof setCanvasTemplate>;
// Rename page
export const setPageTitle = z.object({
	operation: z.literal("setPageTitle"),
	pageId: z.string(),
	newTitle: z.string(),
});
export type SetPageTitleOperation = z.infer<typeof setPageTitle>;
// Set page ordering
export const pageInfo = z.object({
	pageId: z.string(),
	parentPageId: z.string().nullable(),
});
export type PageInfo = z.infer<typeof pageInfo>;
export const setPageOrder = z.object({
	operation: z.literal("setPageOrder"),
	newPageIds: z.array(pageInfo),
});
export type SetPageOrderOperation = z.infer<typeof setPageOrder>;
// Create page
export const createPage = z.object({
	operation: z.literal("createPage"),
	authorId: z.string(),
	title: z.string(),
});
export type CreatePageOperation = z.infer<typeof createPage>;
// Archive page
export const archivePage = z.object({
	operation: z.literal("archivePage"),
	pageId: z.string(),
	archived: z.boolean(),
});
export type ArchivePageOperation = z.infer<typeof archivePage>;
// Duplicate page
export const duplicatePage = z.object({
	operation: z.literal("duplicatePage"),
	sourcePageId: z.string(),
	authorId: z.string(),
});
export type DuplicatePageOperation = z.infer<typeof duplicatePage>;

export const metadataOperation = z.union([
	setCanvasTitle,
	archiveCanvas,
	setCanvasTemplate,
	setPageTitle,
	setPageOrder,
	createPage,
	archivePage,
	duplicatePage,
]);

export type MetadataOperation = z.infer<typeof metadataOperation>;
